/*
 * =========================================================
 * CHEMARRY OFFERS v2.0
 * Shopify Dawn 15.4.1
 *
 * - Hasta 8 promociones
 * - Hasta 6 productos por promoción
 * - Packs completos
 * - Selección individual de variedades
 * - Cantidades mínimas protegidas
 * - Actualización del carrito lateral de Dawn
 * - Navegación y accesibilidad
 * =========================================================
 */

(() => {
  'use strict';

  const PRODUCT_CACHE = new Map();

  const SHOPIFY_ROOT =
    window.Shopify?.routes?.root || '/';

  class ChemarryOffers extends HTMLElement {
    constructor() {
      super();

      this.activeCard = null;
      this.activeConfig = null;
      this.productStates = [];
      this.lastFocusedElement = null;
      this.scrollFrame = null;
      this.toastTimer = null;
      this.isSubmitting = false;

      this.handleDocumentKeydown =
        this.handleDocumentKeydown.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.handleTrackScroll =
        this.handleTrackScroll.bind(this);

      this.handleBlockSelect =
        this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.track = this.querySelector(
        '[data-chem-track]'
      );

      this.previousButton = this.querySelector(
        '[data-chem-prev]'
      );

      this.nextButton = this.querySelector(
        '[data-chem-next]'
      );

      this.dotsContainer = this.querySelector(
        '[data-chem-dots]'
      );

      this.offerCards = Array.from(
        this.querySelectorAll('[data-chem-offer]')
      );

      this.dialog = this.querySelector(
        '[data-chem-dialog]'
      );

      this.dialogSheet = this.querySelector(
        '[data-chem-dialog-sheet]'
      );

      this.dialogTitle = this.querySelector(
        '[data-chem-dialog-title]'
      );

      this.dialogDescription = this.querySelector(
        '[data-chem-dialog-description]'
      );

      this.dialogBadge = this.querySelector(
        '[data-chem-dialog-badge]'
      );

      this.dialogMessage = this.querySelector(
        '[data-chem-dialog-message]'
      );

      this.dialogProducts = this.querySelector(
        '[data-chem-dialog-products]'
      );

      this.dialogSummary = this.querySelector(
        '[data-chem-dialog-summary]'
      );

      this.dialogFooter = this.querySelector(
        '[data-chem-dialog-footer]'
      );

      this.addAllButton = this.querySelector(
        '[data-chem-add-all]'
      );

      this.toast = this.querySelector(
        '[data-chem-toast]'
      );

      this.bindCarousel();
      this.bindOffers();
      this.bindDialog();
      this.buildDots();
      this.updateCarouselState();

      document.addEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      window.addEventListener(
        'resize',
        this.handleResize,
        { passive: true }
      );

      document.addEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );
    }

    disconnectedCallback() {
      document.removeEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      document.removeEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );

      if (this.track) {
        this.track.removeEventListener(
          'scroll',
          this.handleTrackScroll
        );
      }

      window.clearTimeout(this.toastTimer);
    }

    /* =====================================================
       1. UTILIDADES
       ===================================================== */

    query(selector, scope = this) {
      return scope?.querySelector(selector) || null;
    }

    queryAll(selector, scope = this) {
      return scope
        ? Array.from(scope.querySelectorAll(selector))
        : [];
    }

    clamp(value, minimum, maximum) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return minimum;
      }

      return Math.min(
        Math.max(number, minimum),
        maximum
      );
    }

    escapeHTML(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }

    normalizeImageURL(value) {
      if (!value) return '';

      const url = String(value);

      if (url.startsWith('//')) {
        return `https:${url}`;
      }

      return url;
    }

    formatMoney(cents) {
      const numericValue = Number(cents) || 0;

      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(numericValue / 100);
    }

    getVariantLabel(variant) {
      if (!variant) return '';

      if (
        variant.public_title &&
        variant.public_title !== 'Default Title'
      ) {
        return variant.public_title;
      }

      if (
        variant.title &&
        variant.title !== 'Default Title'
      ) {
        return variant.title;
      }

      return 'Única opción';
    }

    getProductButtonLabel(state) {
      if (!state.selectedVariant?.available) {
        return 'Sin stock';
      }

      return `Agregar ${state.quantity}`;
    }

    /* =====================================================
       2. CONFIGURACIÓN
       ===================================================== */

    getCardConfig(card) {
      if (!card) return null;

      const configID = card.dataset.configId;

      if (!configID) return null;

      const configElement =
        document.getElementById(configID);

      if (!configElement) return null;

      try {
        const config = JSON.parse(
          configElement.textContent.trim()
        );

        config.products = Array.isArray(config.products)
          ? config.products.filter(
              (product) =>
                product &&
                product.handle
            )
          : [];

        config.allowIndividualAdd =
          config.allowIndividualAdd === true;

        config.enforceQuantities =
          config.enforceQuantities !== false;

        config.openCart =
          config.openCart !== false;

        return config;
      } catch (error) {
        console.error(
          '[Chemarry Offers] Configuración inválida:',
          error
        );

        return null;
      }
    }

    /* =====================================================
       3. CARGA DE PRODUCTOS
       ===================================================== */

    async fetchProduct(handle) {
      const normalizedHandle =
        String(handle || '').trim();

      if (!normalizedHandle) {
        throw new Error(
          'Hay un producto sin configurar.'
        );
      }

      if (PRODUCT_CACHE.has(normalizedHandle)) {
        return PRODUCT_CACHE.get(normalizedHandle);
      }

      const productURL =
        `${SHOPIFY_ROOT}products/` +
        `${encodeURIComponent(normalizedHandle)}.js`;

      const request = fetch(productURL, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `No pudimos cargar el producto ${normalizedHandle}.`
          );
        }

        return response.json();
      });

      PRODUCT_CACHE.set(
        normalizedHandle,
        request
      );

      try {
        return await request;
      } catch (error) {
        PRODUCT_CACHE.delete(normalizedHandle);
        throw error;
      }
    }

    async loadProducts(config) {
      const configuredProducts =
        config.products || [];

      if (!configuredProducts.length) {
        throw new Error(
          'Esta oferta todavía no tiene productos configurados.'
        );
      }

      return Promise.all(
        configuredProducts.map(
          async (productConfig) => {
            const product =
              await this.fetchProduct(
                productConfig.handle
              );

            return {
              product,
              config: productConfig
            };
          }
        )
      );
    }

    /* =====================================================
       4. CARRUSEL
       ===================================================== */

    bindCarousel() {
      this.previousButton?.addEventListener(
        'click',
        () => this.moveCarousel(-1)
      );

      this.nextButton?.addEventListener(
        'click',
        () => this.moveCarousel(1)
      );

      this.track?.addEventListener(
        'scroll',
        this.handleTrackScroll,
        { passive: true }
      );
    }

    handleTrackScroll() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(
          this.scrollFrame
        );
      }

      this.scrollFrame =
        window.requestAnimationFrame(() => {
          this.updateCarouselState();
        });
    }

    handleResize() {
      this.updateCarouselState();
    }

    getNearestCardIndex() {
      if (
        !this.track ||
        !this.offerCards.length
      ) {
        return 0;
      }

      const trackRectangle =
        this.track.getBoundingClientRect();

      let nearestIndex = 0;
      let nearestDistance =
        Number.POSITIVE_INFINITY;

      this.offerCards.forEach(
        (card, index) => {
          const cardRectangle =
            card.getBoundingClientRect();

          const distance = Math.abs(
            cardRectangle.left -
              trackRectangle.left
          );

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        }
      );

      return nearestIndex;
    }

    scrollToCard(index) {
      const targetIndex = this.clamp(
        index,
        0,
        Math.max(
          this.offerCards.length - 1,
          0
        )
      );

      const targetCard =
        this.offerCards[targetIndex];

      if (!targetCard) return;

      targetCard.scrollIntoView({
        behavior: window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
          ? 'auto'
          : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }

    moveCarousel(direction) {
      const currentIndex =
        this.getNearestCardIndex();

      this.scrollToCard(
        currentIndex + direction
      );
    }

    buildDots() {
      if (!this.dotsContainer) return;

      if (this.offerCards.length <= 1) {
        this.dotsContainer.hidden = true;
        return;
      }

      this.dotsContainer.hidden = false;
      this.dotsContainer.innerHTML = '';

      this.offerCards.forEach(
        (card, index) => {
          const dot =
            document.createElement('button');

          dot.type = 'button';
          dot.className =
            'chemarry-offers__dot';

          dot.dataset.chemDot =
            String(index);

          dot.setAttribute(
            'aria-label',
            `Ir a la oferta ${index + 1}`
          );

          dot.addEventListener(
            'click',
            () => this.scrollToCard(index)
          );

          this.dotsContainer.appendChild(dot);
        }
      );
    }

    updateCarouselState() {
      if (!this.offerCards.length) return;

      const activeIndex =
        this.getNearestCardIndex();

      const dots = this.queryAll(
        '[data-chem-dot]',
        this.dotsContainer
      );

      dots.forEach((dot, index) => {
        const isActive =
          index === activeIndex;

        dot.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          dot.setAttribute(
            'aria-current',
            'true'
          );
        } else {
          dot.removeAttribute(
            'aria-current'
          );
        }
      });

      if (!this.track) return;

      const maximumScroll =
        this.track.scrollWidth -
        this.track.clientWidth;

      const atStart =
        this.track.scrollLeft <= 4;

      const atEnd =
        this.track.scrollLeft >=
        maximumScroll - 4;

      if (this.previousButton) {
        this.previousButton.disabled =
          atStart ||
          maximumScroll <= 4;
      }

      if (this.nextButton) {
        this.nextButton.disabled =
          atEnd ||
          maximumScroll <= 4;
      }
    }

    handleBlockSelect(event) {
      const selectedBlock = event.target;

      if (
        !selectedBlock ||
        !this.contains(selectedBlock)
      ) {
        return;
      }

      const card = selectedBlock.matches(
        '[data-chem-offer]'
      )
        ? selectedBlock
        : selectedBlock.querySelector(
            '[data-chem-offer]'
          );

      if (!card) return;

      const index =
        this.offerCards.indexOf(card);

      if (index >= 0) {
        this.scrollToCard(index);
      }
    }

    /* =====================================================
       5. TARJETAS
       ===================================================== */

    bindOffers() {
      this.offerCards.forEach((card) => {
        card.addEventListener(
          'click',
          () => this.handleCardClick(card)
        );
      });
    }

    handleCardClick(card) {
      if (
        card.getAttribute(
          'aria-disabled'
        ) === 'true'
      ) {
        return;
      }

      const config =
        this.getCardConfig(card);

      if (!config) {
        this.showToast(
          'Esta oferta todavía no está configurada.',
          'error'
        );

        return;
      }

      if (
        config.action === 'link' &&
        config.url
      ) {
        window.location.assign(config.url);
        return;
      }

      this.openOffer(card, config);
    }

    async openOffer(card, config) {
      if (
        !this.dialog ||
        !this.dialogProducts
      ) {
        return;
      }

      this.activeCard = card;
      this.activeConfig = config;
      this.productStates = [];
      this.lastFocusedElement =
        document.activeElement;

      this.setDialogContent(config);
      this.showDialog();
      this.renderLoading();

      try {
        const loadedProducts =
          await this.loadProducts(config);

        if (
          this.activeConfig !== config ||
          !this.isDialogOpen()
        ) {
          return;
        }

        this.renderProducts(
          loadedProducts,
          config
        );
      } catch (error) {
        console.error(
          '[Chemarry Offers] Error cargando la oferta:',
          error
        );

        this.renderError(
          error.message ||
            'No pudimos cargar esta oferta.'
        );
      }
    }

    setDialogContent(config) {
      const isIndividual =
        config.allowIndividualAdd === true;

      this.dialog?.classList.toggle(
        'is-individual',
        isIndividual
      );

      if (this.dialogTitle) {
        this.dialogTitle.textContent =
          config.title || 'Oferta';
      }

      if (this.dialogDescription) {
        this.dialogDescription.textContent =
          config.description || '';

        this.dialogDescription.hidden =
          !config.description;
      }

      if (this.dialogBadge) {
        this.dialogBadge.textContent =
          config.badge || 'Oferta';

        this.dialogBadge.hidden =
          !config.badge;
      }

      if (this.dialogMessage) {
        const message =
          config.discountMessage ||
          this.getDefaultDiscountMessage(
            config
          );

        this.dialogMessage.textContent =
          message;

        this.dialogMessage.hidden =
          !message;
      }

      if (this.dialogSummary) {
        this.dialogSummary.textContent =
          this.getSummaryText(config);
      }

      if (this.addAllButton) {
        this.addAllButton.hidden =
          isIndividual;

        this.addAllButton.disabled = true;

        this.addAllButton.classList.remove(
          'is-success'
        );

        this.addAllButton.textContent =
          config.addAllLabel ||
          'Agregar oferta';
      }
    }

    getDefaultDiscountMessage(config) {
      switch (config.discountMethod) {
        case 'automatic':
          return 'El descuento se aplicará automáticamente cuando el carrito cumpla las condiciones.';

        case 'code':
          return config.discountCode
            ? `El código ${config.discountCode} se aplicará al continuar.`
            : 'El código promocional se aplicará al continuar.';

        case 'sale':
          return 'El precio promocional ya está aplicado al producto.';

        default:
          return '';
      }
    }

    getSummaryText(config) {
      if (config.allowIndividualAdd) {
        return 'Selecciona una variedad. Agregaremos la cantidad necesaria para activar la promoción.';
      }

      if (config.discountMethod === 'automatic') {
        return 'Agregaremos las cantidades necesarias para activar la promoción.';
      }

      if (config.discountMethod === 'code') {
        return 'Agregaremos los productos y aplicaremos el código promocional.';
      }

      return 'Revisa los productos antes de agregarlos al carrito.';
    }

    /* =====================================================
       6. DIÁLOGO Y ACCESIBILIDAD
       ===================================================== */

    bindDialog() {
      if (!this.dialog) return;

      this.queryAll(
        '[data-chem-dialog-close]',
        this.dialog
      ).forEach((button) => {
        button.addEventListener(
          'click',
          () => this.closeDialog()
        );
      });

      const viewCartButton = this.query(
        '[data-chem-view-cart]',
        this.dialog
      );

      viewCartButton?.addEventListener(
        'click',
        () => {
          window.location.assign(
            `${SHOPIFY_ROOT}cart`
          );
        }
      );

      this.addAllButton?.addEventListener(
        'click',
        () => this.addEntireOffer()
      );
    }

    showDialog() {
      if (!this.dialog) return;

      this.dialog.hidden = false;

      this.dialog.setAttribute(
        'aria-hidden',
        'false'
      );

      document.documentElement.classList.add(
        'chemarry-offers-lock'
      );

      document.body.classList.add(
        'chemarry-offers-lock'
      );

      window.requestAnimationFrame(() => {
        this.dialogSheet?.focus();
      });
    }

    closeDialog(options = {}) {
      const restoreFocus =
        options.restoreFocus !== false;

      if (!this.dialog) return;

      this.dialog.hidden = true;

      this.dialog.setAttribute(
        'aria-hidden',
        'true'
      );

      this.dialog.classList.remove(
        'is-individual'
      );

      document.documentElement.classList.remove(
        'chemarry-offers-lock'
      );

      document.body.classList.remove(
        'chemarry-offers-lock'
      );

      this.productStates = [];
      this.activeConfig = null;
      this.activeCard = null;
      this.isSubmitting = false;

      if (this.dialogProducts) {
        this.dialogProducts.innerHTML = '';
      }

      if (
        restoreFocus &&
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus ===
          'function'
      ) {
        this.lastFocusedElement.focus();
      }
    }

    isDialogOpen() {
      return Boolean(
        this.dialog &&
        !this.dialog.hidden &&
        this.dialog.getAttribute(
          'aria-hidden'
        ) !== 'true'
      );
    }

    handleDocumentKeydown(event) {
      if (!this.isDialogOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
        return;
      }

      if (event.key === 'Tab') {
        this.trapDialogFocus(event);
      }
    }

    trapDialogFocus(event) {
      if (!this.dialogSheet) return;

      const focusableElements =
        this.queryAll(
          [
            'button:not([disabled]):not([hidden])',
            '[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(','),
          this.dialogSheet
        ).filter(
          (element) =>
            element.offsetParent !== null &&
            element.getAttribute(
              'aria-hidden'
            ) !== 'true'
        );

      if (!focusableElements.length) {
        event.preventDefault();
        this.dialogSheet.focus();
        return;
      }

      const firstElement =
        focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    /* =====================================================
       7. CARGA Y ERRORES
       ===================================================== */

    renderLoading() {
      if (!this.dialogProducts) return;

      this.dialogProducts.innerHTML = `
        <div class="chemarry-offers-dialog__loading">
          <span
            class="chemarry-offers-dialog__spinner"
            aria-hidden="true"
          ></span>

          <span>Cargando productos…</span>
        </div>
      `;
    }

    renderError(message) {
      if (!this.dialogProducts) return;

      this.dialogProducts.innerHTML = `
        <div
          class="chemarry-offers-dialog__error"
          role="alert"
        >
          ${this.escapeHTML(message)}
        </div>
      `;

      if (this.addAllButton) {
        this.addAllButton.hidden = true;
      }
    }

    /* =====================================================
       8. PRODUCTOS DEL PANEL
       ===================================================== */

    renderProducts(
      loadedProducts,
      config
    ) {
      if (!this.dialogProducts) return;

      this.dialogProducts.innerHTML = '';
      this.productStates = [];

      loadedProducts.forEach(
        (
          {
            product,
            config: productConfig
          },
          index
        ) => {
          const state =
            this.createProductState(
              product,
              productConfig,
              config,
              index
            );

          this.productStates.push(state);

          const productElement =
            this.createProductElement(state);

          this.dialogProducts.appendChild(
            productElement
          );

          state.element = productElement;

          this.bindProductElement(state);
          this.updateProductControls(state);
        }
      );

      this.updateAddAllButton();
    }

    createProductState(
      product,
      productConfig,
      offerConfig,
      index
    ) {
      const variants =
        Array.isArray(product.variants)
          ? product.variants
          : [];

      const configuredVariantID =
        String(
          productConfig.variantId || ''
        );

      let selectedVariant = null;

            /* HOTFIX-OFFERS-NOFALLBACK-01: nunca sustituir la variante
         por disponibilidad. Configurada = exacta o nada;
         sin configurar = primera por posicion, aunque este agotada. */
      if (configuredVariantID) {
        selectedVariant =
          variants.find(
            (variant) =>
              String(variant.id) ===
              configuredVariantID
          ) || null;
      } else {
        selectedVariant =
          variants[0] || null;
      }

      const configuredQuantity =
        this.clamp(
          productConfig.quantity || 1,
          1,
          99
        );

      const enforceQuantity =
        offerConfig.enforceQuantities !==
        false;

      return {
        index,
        product,
        productConfig,
        variants,
        selectedVariant,
        selectedVariantID:
          selectedVariant?.id || null,

        minimumQuantity: enforceQuantity
          ? configuredQuantity
          : 1,

        quantity: configuredQuantity,

        showVariants:
          productConfig.showVariants !==
            false &&
          variants.length > 1,

        allowQuantity:
          productConfig.allowQuantity !==
          false,

        allowIndividualAdd:
          offerConfig.allowIndividualAdd ===
          true,

        element: null
      };
    }

    createProductElement(state) {
      const product = state.product;
      const variant = state.selectedVariant;

      const imageURL =
        this.normalizeImageURL(
          variant?.featured_image?.src ||
          product.featured_image ||
          product.images?.[0] ||
          ''
        );

      const variantsHTML =
        state.showVariants
          ? this.getVariantsHTML(state)
          : '';

      const quantityHTML =
        state.allowQuantity
          ? `
            <div
              class="chemarry-offers-dialog__quantity"
              aria-label="Cantidad"
            >
              <button
                type="button"
                class="chemarry-offers-dialog__quantity-button"
                data-chem-quantity-minus
                aria-label="Disminuir cantidad"
              >
                −
              </button>

              <span
                class="chemarry-offers-dialog__quantity-value"
                data-chem-quantity-value
                aria-live="polite"
              >
                ${state.quantity}
              </span>

              <button
                type="button"
                class="chemarry-offers-dialog__quantity-button"
                data-chem-quantity-plus
                aria-label="Aumentar cantidad"
              >
                +
              </button>
            </div>
          `
          : '';

      const individualButtonHTML =
        state.allowIndividualAdd
          ? `
            <button
              type="button"
              class="chemarry-offers-dialog__add"
              data-chem-add-product
              ${
                variant?.available
                  ? ''
                  : 'disabled'
              }
            >
              ${this.escapeHTML(
                this.getProductButtonLabel(
                  state
                )
              )}
            </button>
          `
          : '';

      const element =
        document.createElement('article');

      element.className =
        'chemarry-offers-dialog__product';

      element.dataset.productIndex =
        String(state.index);

      element.innerHTML = `
        ${
          imageURL
            ? `
              <img
                class="chemarry-offers-dialog__product-image"
                src="${this.escapeAttribute(
                  imageURL
                )}"
                alt="${this.escapeAttribute(
                  product.title
                )}"
                width="144"
                height="144"
                loading="lazy"
              >
            `
            : `
              <div
                class="chemarry-offers-dialog__product-image"
                aria-hidden="true"
              ></div>
            `
        }

        <div class="chemarry-offers-dialog__product-main">
          <h3 class="chemarry-offers-dialog__product-title">
            ${this.escapeHTML(
              product.title
            )}
          </h3>

          <div
            class="chemarry-offers-dialog__product-price"
            data-chem-product-price
          >
            ${this.getVariantPriceHTML(
              variant
            )}
          </div>

          <div data-chem-product-stock>
            ${this.getStockHTML(variant)}
          </div>

          ${variantsHTML}

          <div class="chemarry-offers-dialog__actions">
            ${quantityHTML}
            ${individualButtonHTML}
          </div>
        </div>
      `;

      return element;
    }

    getVariantPriceHTML(variant) {
      if (!variant) {
        return '<strong>Precio no disponible</strong>';
      }

      const price =
        Number(variant.price) || 0;

      const compareAtPrice =
        Number(
          variant.compare_at_price
        ) || 0;

      if (compareAtPrice > price) {
        return `
          <del>
            ${this.formatMoney(
              compareAtPrice
            )}
          </del>

          <strong>
            ${this.formatMoney(price)}
          </strong>
        `;
      }

      return `
        <strong>
          ${this.formatMoney(price)}
        </strong>
      `;
    }

    getStockHTML(variant) {
      if (variant?.available) {
        return `
          <span class="chemarry-offers-dialog__stock">
            Disponible
          </span>
        `;
      }

      return `
        <span
          class="chemarry-offers-dialog__stock is-unavailable"
        >
          Sin stock
        </span>
      `;
    }

    getVariantsHTML(state) {
      const buttons = state.variants
        .map((variant) => {
          const isActive =
            String(variant.id) ===
            String(
              state.selectedVariantID
            );

          return `
            <button
              type="button"
              class="chemarry-offers-dialog__variant${
                isActive
                  ? ' is-active'
                  : ''
              }"
              data-chem-variant-id="${
                variant.id
              }"
              aria-pressed="${
                isActive
                  ? 'true'
                  : 'false'
              }"
              ${
                variant.available
                  ? ''
                  : 'disabled aria-disabled="true"'
              }
            >
              ${this.escapeHTML(
                this.getVariantLabel(
                  variant
                )
              )}
            </button>
          `;
        })
        .join('');

      return `
        <div
          class="chemarry-offers-dialog__variants"
          aria-label="Opciones del producto"
        >
          ${buttons}
        </div>
      `;
    }

    /* =====================================================
       9. CONTROLES DEL PRODUCTO
       ===================================================== */

    bindProductElement(state) {
      if (!state.element) return;

      const minusButton = this.query(
        '[data-chem-quantity-minus]',
        state.element
      );

      const plusButton = this.query(
        '[data-chem-quantity-plus]',
        state.element
      );

      const addButton = this.query(
        '[data-chem-add-product]',
        state.element
      );

      minusButton?.addEventListener(
        'click',
        () => {
          this.setProductQuantity(
            state,
            state.quantity - 1
          );
        }
      );

      plusButton?.addEventListener(
        'click',
        () => {
          this.setProductQuantity(
            state,
            state.quantity + 1
          );
        }
      );

      this.queryAll(
        '[data-chem-variant-id]',
        state.element
      ).forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            if (
              button.disabled ||
              button.getAttribute(
                'aria-disabled'
              ) === 'true'
            ) {
              return;
            }

            this.setProductVariant(
              state,
              button.dataset
                .chemVariantId
            );
          }
        );
      });

      addButton?.addEventListener(
        'click',
        () => {
          this.addSingleProduct(
            state,
            addButton
          );
        }
      );
    }

    setProductQuantity(
      state,
      quantity
    ) {
      state.quantity = this.clamp(
        quantity,
        state.minimumQuantity,
        99
      );

      this.updateProductControls(state);
      this.updateAddAllButton();
    }

    setProductVariant(
      state,
      variantID
    ) {
      const variant =
        state.variants.find(
          (item) =>
            String(item.id) ===
            String(variantID)
        );

      if (
        !variant ||
        !variant.available
      ) {
        return;
      }

      state.selectedVariant = variant;
      state.selectedVariantID =
        variant.id;

      this.queryAll(
        '[data-chem-variant-id]',
        state.element
      ).forEach((button) => {
        const isActive =
          String(
            button.dataset.chemVariantId
          ) === String(variant.id);

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          isActive
            ? 'true'
            : 'false'
        );
      });

      const priceElement = this.query(
        '[data-chem-product-price]',
        state.element
      );

      if (priceElement) {
        priceElement.innerHTML =
          this.getVariantPriceHTML(
            variant
          );
      }

      const stockElement = this.query(
        '[data-chem-product-stock]',
        state.element
      );

      if (stockElement) {
        stockElement.innerHTML =
          this.getStockHTML(variant);
      }

      this.updateProductControls(state);
      this.updateAddAllButton();
    }

    updateProductControls(state) {
      if (!state.element) return;

      const quantityValue = this.query(
        '[data-chem-quantity-value]',
        state.element
      );

      const minusButton = this.query(
        '[data-chem-quantity-minus]',
        state.element
      );

      const addButton = this.query(
        '[data-chem-add-product]',
        state.element
      );

      if (quantityValue) {
        quantityValue.textContent =
          String(state.quantity);
      }

      if (minusButton) {
        minusButton.disabled =
          state.quantity <=
          state.minimumQuantity;
      }

      if (addButton) {
        addButton.disabled =
          !state.selectedVariant
            ?.available;

        addButton.textContent =
          this.getProductButtonLabel(
            state
          );
      }
    }

    /* =====================================================
       10. CARRITO
       ===================================================== */

    getAvailableItems() {
      return this.productStates
        .filter(
          (state) =>
            state.selectedVariant &&
            state.selectedVariant.available
        )
        .map((state) => ({
          id: Number(
            state.selectedVariantID
          ),

          quantity: Number(
            state.quantity
          )
        }));
    }

    async addSingleProduct(
      state,
      button
    ) {
      if (
        this.isSubmitting ||
        !state.selectedVariant
          ?.available
      ) {
        return;
      }

      this.isSubmitting = true;

      const normalLabel =
        this.getProductButtonLabel(
          state
        );

      button.disabled = true;
      button.textContent = 'Agregando…';

      try {
        const response =
          await this.addItemsToCart([
            {
              id: Number(
                state.selectedVariantID
              ),

              quantity: Number(
                state.quantity
              )
            }
          ]);

        button.classList.add(
          'is-success'
        );

        button.textContent =
          'Agregado';

        const config =
          this.activeConfig;

        this.showToast(
          config?.successMessage ||
            'Promoción agregada al carrito.',
          'success'
        );

        if (
          config?.discountMethod ===
            'code' &&
          config.discountCode
        ) {
          this.redirectWithDiscount(
            config
          );

          return;
        }

        const shouldOpenCart =
          config?.openCart !== false;

        this.closeDialog({
          restoreFocus:
            !shouldOpenCart
        });

        await this.refreshCart(
          response,
          shouldOpenCart
        );
      } catch (error) {
        console.error(
          '[Chemarry Offers] Error agregando el producto:',
          error
        );

        button.classList.remove(
          'is-success'
        );

        button.textContent =
          normalLabel;

        button.disabled = false;

        this.showToast(
          error.message ||
            'No pudimos agregar la promoción.',
          'error'
        );
      } finally {
        this.isSubmitting = false;
      }
    }

    async addEntireOffer() {
      if (
        this.isSubmitting ||
        !this.activeConfig ||
        !this.addAllButton
      ) {
        return;
      }

      /*
       * Seguridad: en una oferta de variedades nunca
       * permitimos agregar todos los productos juntos.
       */

      if (
        this.activeConfig
          .allowIndividualAdd === true
      ) {
        return;
      }

      const items =
        this.getAvailableItems();

      if (!items.length) {
        this.showToast(
          'No hay productos disponibles en esta oferta.',
          'error'
        );

        return;
      }

      this.isSubmitting = true;

      const originalText =
        this.addAllButton.textContent;

      this.addAllButton.disabled = true;
      this.addAllButton.textContent =
        'Agregando…';

      try {
        const response =
          await this.addItemsToCart(
            items
          );

        this.addAllButton.classList.add(
          'is-success'
        );

        this.addAllButton.textContent =
          'Oferta agregada';

        const config =
          this.activeConfig;

        this.showToast(
          config.successMessage ||
            'Oferta agregada al carrito.',
          'success'
        );

        if (
          config.discountMethod ===
            'code' &&
          config.discountCode
        ) {
          this.redirectWithDiscount(
            config
          );

          return;
        }

        const shouldOpenCart =
          config.openCart !== false;

        this.closeDialog({
          restoreFocus:
            !shouldOpenCart
        });

        await this.refreshCart(
          response,
          shouldOpenCart
        );
      } catch (error) {
        console.error(
          '[Chemarry Offers] Error agregando la oferta:',
          error
        );

        this.addAllButton.classList.remove(
          'is-success'
        );

        this.addAllButton.textContent =
          originalText;

        this.addAllButton.disabled = false;

        this.showToast(
          error.message ||
            'No pudimos agregar la oferta.',
          'error'
        );
      } finally {
        this.isSubmitting = false;
      }
    }

    updateAddAllButton() {
      if (!this.addAllButton) return;

      const isIndividual =
        this.activeConfig
          ?.allowIndividualAdd === true;

      if (isIndividual) {
        this.addAllButton.hidden = true;
        this.addAllButton.disabled = true;
        return;
      }

      const availableItems =
        this.getAvailableItems();

      const hasItems =
        availableItems.length > 0;

      this.addAllButton.hidden = false;
      this.addAllButton.disabled =
        !hasItems;

      if (!hasItems) {
        this.addAllButton.textContent =
          'Sin stock disponible';
      } else {
        this.addAllButton.textContent =
          this.activeConfig
            ?.addAllLabel ||
          'Agregar oferta';
      }
    }

    redirectWithDiscount(config) {
      const redirectPath =
        config.discountRedirect ||
        '/cart';

      const discountURL =
        `${SHOPIFY_ROOT}discount/` +
        `${encodeURIComponent(
          config.discountCode
        )}` +
        `?redirect=${encodeURIComponent(
          redirectPath
        )}`;

      window.setTimeout(() => {
        window.location.assign(
          discountURL
        );
      }, 300);
    }

    async addItemsToCart(items) {
      const validItems = items
        .filter(
          (item) =>
            Number(item.id) > 0 &&
            Number(item.quantity) > 0
        )
        .map((item) => ({
          id: Number(item.id),
          quantity: Number(
            item.quantity
          )
        }));

      if (!validItems.length) {
        throw new Error(
          'No hay productos válidos para agregar.'
        );
      }

      const response = await fetch(
        `${SHOPIFY_ROOT}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json'
          },

          body: JSON.stringify({
            items: validItems,

            sections: [
              'cart-drawer',
              'cart-icon-bubble'
            ],

            sections_url:
              window.location.pathname +
              window.location.search
          })
        }
      );

      let data = {};

      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'No pudimos agregar los productos al carrito.'
        );
      }

      return data;
    }

    /* =====================================================
       11. ACTUALIZACIÓN DEL CARRITO DAWN
       ===================================================== */

    async refreshCart(
      response,
      openDrawer = true
    ) {
      const cartDrawer =
        document.querySelector(
          'cart-drawer'
        );

      if (
        cartDrawer &&
        typeof cartDrawer
          .renderContents ===
          'function' &&
        response?.sections
      ) {
        try {
          cartDrawer.classList.remove(
            'is-empty'
          );

          const drawerInner =
            cartDrawer.querySelector(
              '.drawer__inner'
            );

          const drawerItems =
            cartDrawer.querySelector(
              'cart-drawer-items'
            );

          drawerInner?.classList.remove(
            'is-empty'
          );

          drawerItems?.classList.remove(
            'is-empty'
          );

          cartDrawer.renderContents(
            response
          );

          cartDrawer.classList.remove(
            'is-empty'
          );

          cartDrawer
            .querySelector(
              'cart-drawer-items'
            )
            ?.classList.remove(
              'is-empty'
            );

          if (
            openDrawer &&
            typeof cartDrawer.open ===
              'function'
          ) {
            window.setTimeout(() => {
              cartDrawer.classList.remove(
                'is-empty'
              );

              cartDrawer.open();
            }, 100);
          }

          return;
        } catch (error) {
          console.warn(
            '[Chemarry Offers] Se utilizará la actualización alternativa del carrito.',
            error
          );
        }
      }

      await this.refreshCartFallback(
        openDrawer
      );
    }

    async refreshCartFallback(
      openDrawer
    ) {
      try {
        const requestURL =
          `${window.location.pathname}` +
          `${window.location.search}` +
          `${
            window.location.search
              ? '&'
              : '?'
          }sections=cart-drawer,cart-icon-bubble`;

        const response = await fetch(
          requestURL,
          {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) return;

        const sections =
          await response.json();

        this.replaceSectionHTML(
          'cart-icon-bubble',
          sections[
            'cart-icon-bubble'
          ]
        );

        this.replaceSectionHTML(
          'cart-drawer',
          sections['cart-drawer']
        );

        if (openDrawer) {
          const cartDrawer =
            document.querySelector(
              'cart-drawer'
            );

          if (
            cartDrawer &&
            typeof cartDrawer.open ===
              'function'
          ) {
            window.setTimeout(() => {
              cartDrawer.classList.remove(
                'is-empty'
              );

              cartDrawer.open();
            }, 100);
          }
        }
      } catch (error) {
        console.warn(
          '[Chemarry Offers] No pudimos actualizar visualmente el carrito.',
          error
        );
      }
    }

    replaceSectionHTML(
      sectionID,
      html
    ) {
      if (!html) return;

      const parser =
        new DOMParser();

      const parsedDocument =
        parser.parseFromString(
          html,
          'text/html'
        );

      if (
        sectionID ===
        'cart-icon-bubble'
      ) {
        const currentElement =
          document.getElementById(
            'cart-icon-bubble'
          );

        const newElement =
          parsedDocument.getElementById(
            'cart-icon-bubble'
          );

        if (
          currentElement &&
          newElement
        ) {
          currentElement.replaceWith(
            newElement
          );
        }

        return;
      }

      if (sectionID === 'cart-drawer') {
        const currentDrawer =
          document.querySelector(
            'cart-drawer'
          );

        const newDrawer =
          parsedDocument.querySelector(
            'cart-drawer'
          );

        if (
          currentDrawer &&
          newDrawer
        ) {
          currentDrawer.replaceWith(
            newDrawer
          );
        }
      }
    }

    /* =====================================================
       12. MENSAJES
       ===================================================== */

    showToast(
      message,
      type = 'default'
    ) {
      if (
        !this.toast ||
        !message
      ) {
        return;
      }

      window.clearTimeout(
        this.toastTimer
      );

      this.toast.textContent =
        message;

      this.toast.hidden = false;

      this.toast.classList.toggle(
        'is-error',
        type === 'error'
      );

      this.toast.classList.toggle(
        'is-success',
        type === 'success'
      );

      this.toastTimer =
        window.setTimeout(() => {
          this.toast.hidden = true;

          this.toast.classList.remove(
            'is-error',
            'is-success'
          );
        }, 3200);
    }
  }

  if (
    !customElements.get(
      'chemarry-offers'
    )
  ) {
    customElements.define(
      'chemarry-offers',
      ChemarryOffers
    );
  }
})();